import React, { useMemo, useState } from 'react';

import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import DialogContent from '@mui/material/DialogContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import { makeStyles } from 'tss-react/mui';

import { accountsUtils } from 'app-components/flux/accounts';
import { chartOfAccountsSelectors } from 'app-components/flux/chart-of-accounts';
import { scheduledTransactionsSelectors, scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import type { ScheduledTransaction } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';
import store from 'app-components/utils/redux-store';
import ConnectedAvatar from 'components/ConnectedAvatar';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogTitle from 'components/Dialogs/StdDialogTitle';
import StandardDrawer from 'components/Drawers/StandardDrawer';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import QSegmentedTabs from 'components/QSegmentedTabs';
import AmountField, { ShowColorEnum, ShowSignEnum } from 'components/QuickenControls/AmountField';
import SearchBox from 'components/SearchBox';
import ZeroStateView from 'components/ZeroStateView';
import { useAppSelector } from 'hooks/redux';

const LIST_MIN_HEIGHT = 400;
const LIST_MAX_HEIGHT = 572;
const SCHEDULED_ITEM_STRIDE = 76; // 72px row + 4px margin

const useStyles = makeStyles()((theme) => ({
  dialogRoot: {
    padding: '0px !important',
    display: 'flex',
    flexDirection: 'column',
  },
  searchRow: {
    display: 'flex',
    width: '100%',
    padding: theme.spacing(1, 2),
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'nowrap',
  },
  typeSegmented: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    overflowX: 'auto',
    '& .MuiToggleButtonGroup-root': {
      flexWrap: 'nowrap',
      width: 'max-content',
    },
    '& .MuiToggleButton-root': {
      whiteSpace: 'nowrap',
    },
  },
  search: {
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'flex-end',
  },
  searchBar: {
    width: 242,
    height: 36,
    margin: 0,
    paddingLeft: 4,
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    '&:hover': {
      bordercolor: theme.palette.text.primary,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
  },
  searchInput: {
    fontSize: '14px',
    lineHeight: 1.43,
    fontWeight: 'normal',
    color: theme.palette.text.primary,
    '&:-webkit-autofill': {
      WebkitBackgroundClip: 'text',
    },
  },
  containerListItem: {
    height: 72,
    boxShadow: theme.shadows[1],
    borderRadius: 8,
    margin: theme.spacing(0.5, 0),
  },
  listItemButton: {
    borderRadius: 7,
  },
  actionRow: {
    display: 'flex',
    padding: theme.spacing(2),
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.border.light}`,
  },
}));

type TransactionToLinkSummaryProps = {
  txn?: CashFlowTransaction;
};

const ScheduledTransactionAvatar = ({ scheduledTransaction }: { scheduledTransaction: ScheduledTransaction }) => (
  <ConnectedAvatar seriesIcon model={scheduledTransaction} size={40} />
);

const TransactionToLinkSummary = ({ txn }: TransactionToLinkSummaryProps): React.JSX.Element => {
  const postedOnLabel =
    txn?.postedOn && DateTime.fromISO(txn.postedOn).isValid
      ? DateTime.fromISO(txn.postedOn).toLocaleString(DateTime.DATE_MED)
      : '';

  return (
    <Stack direction='row' gap={1} sx={{ pt: 2, px: 2, pb: 0.5, alignItems: 'center' }}>
      <Typography variant='paragraphRegular' color='text.tertiary' noWrap>
        Transaction to link:
      </Typography>
      <Stack
        direction='row'
        alignItems='center'
        gap={1}
        sx={{ minWidth: 0, flex: 1, flexWrap: 'nowrap' }}
        divider={
          <Typography variant='paragraphRegular' color='text.disabled'>
            ·
          </Typography>
        }
      >
        <Typography variant='paragraphMedium' noWrap>
          {txn?.payee}
        </Typography>
        <AmountField
          value={txn?.amount}
          textProps={{ variant: 'captionRegular' }}
          showSign={ShowSignEnum.POSITIVE_ONLY}
          showColor={ShowColorEnum.POSITIVE_ONLY}
          variant='captionRegular'
        />
        <Typography variant='captionRegular' color='text.secondary' noWrap>
          {postedOnLabel}
        </Typography>
        <Typography variant='captionRegular' color='text.secondary' noWrap>
          {accountsUtils.getAccountString(txn?.accountId)}
        </Typography>
      </Stack>
    </Stack>
  );
};

const { modelTypes } = scheduledTransactionsSelectors;

const TYPE_FILTER_OPTIONS = [
  { key: 'all', value: modelTypes.ALL, label: 'All' },
  { key: 'bills', value: modelTypes.BILLS, label: modelTypes.BILLS },
  { key: 'income', value: modelTypes.INCOME, label: modelTypes.INCOME },
  { key: 'transfers', value: modelTypes.TRANSFERS, label: modelTypes.TRANSFERS },
];

type SelectScheduledTransactionDialogProps = {
  modelsOverride?: Record<string, unknown>;
  onBack?: () => void;
  onClose?: () => void;
  onCreate?: () => void;
  onSelected?: (scheduledTransaction: ScheduledTransaction) => void;
  open?: boolean;
  rootModalDrawer?: RootModalDrawerOptions;
  showBackButton?: boolean;
  txn?: CashFlowTransaction;
};

const evaluateBaseTxnType = (txn?: CashFlowTransaction): string => {
  const state = store?.getState();
  if (!state) {
    return scheduledTransactionsSelectors.modelTypes.ALL;
  }
  let type: string = scheduledTransactionsSelectors.modelTypes.ALL;
  const isIncome = transactionsSelectors.isIncomeTxn(state, txn);
  if (isIncome) {
    type = scheduledTransactionsSelectors.modelTypes.INCOME;
  } else {
    const isTransfer = transactionsUtils.isUITransferTxn(txn);
    if (isTransfer) {
      type = scheduledTransactionsSelectors.modelTypes.TRANSFERS;
    } else {
      const isCredit = chartOfAccountsSelectors.isCreditBill(state, txn?.accountId, txn?.coa);
      if (isCredit) {
        type = scheduledTransactionsSelectors.modelTypes.BILLS;
      }
    }
  }
  return type;
};

const SelectScheduledTransactionDialog = (props: SelectScheduledTransactionDialogProps): React.JSX.Element => {
  const { classes } = useStyles();
  const { onSelected, onCreate, modelsOverride: _modelsOverride, rootModalDrawer, txn, ...otherProps } = props;
  const { onBack, onClose, open, showBackButton } = otherProps;

  const [search, setSearch] = useState<string | undefined>();
  const [type, setType] = useState(() => evaluateBaseTxnType(txn));

  const changeType = (_event: React.MouseEvent<HTMLElement>, newType: string | null): void => {
    if (newType) {
      setType(newType);
    }
  };

  const scheduledTransactions = useAppSelector((state) =>
    scheduledTransactionsSelectors.typeFilteredScheduledTransactions(state, type),
  );

  const listHeight = Math.min(
    Math.max(scheduledTransactions.size * SCHEDULED_ITEM_STRIDE - 4, LIST_MIN_HEIGHT),
    LIST_MAX_HEIGHT,
  );

  const filteredScheduledTransactions = useMemo(
    () =>
      scheduledTransactions
        .filter((scheduledTransaction: ScheduledTransaction) => {
          const { transaction } = scheduledTransaction;
          if (!transaction) {
            return false;
          }

          let include = true;
          if (search) {
            include = (transaction.payee ?? '').toLowerCase().includes(search);
            if (!include) {
              const frequencyAlias = scheduledTransactionsUtils.frequencyAliasFromRecurrence(
                scheduledTransaction.recurrence,
              );
              include = frequencyAlias?.toLowerCase()?.includes(search);
            }
            if (!include) {
              include = String(transaction.amount).includes(search);
            }
            if (!include) {
              const accountString = accountsUtils.getAccountString(transaction.accountId);
              include = Boolean(accountString?.toLowerCase()?.includes(search));
            }
          }
          return include;
        })
        .toList()
        .sort((st1: ScheduledTransaction, st2: ScheduledTransaction) =>
          st1.isCompleted !== st2.isCompleted
            ? Number(Boolean(st1.isCompleted)) || -1
            : (st1.transaction?.payee ?? '').localeCompare(st2.transaction?.payee ?? '', undefined, {
                sensitivity: 'base',
                numeric: true,
                caseFirst: 'upper',
              }),
        ),
    [scheduledTransactions, search],
  );

  const content = (
    <>
      <DialogContent className={classes.dialogRoot}>
        <TransactionToLinkSummary txn={txn} />
        <div className={classes.searchRow}>
          <div className={classes.typeSegmented}>
            <QSegmentedTabs
              id='scheduled-txn-type-filter'
              size='medium'
              value={type}
              onChange={changeType}
              aria-label='Recurring transaction type'
            >
              {TYPE_FILTER_OPTIONS.map(({ key, value, label }) => (
                <ToggleButton key={key} value={value} id={`scheduled-txn-type-${key}`}>
                  {label}
                </ToggleButton>
              ))}
            </QSegmentedTabs>
          </div>
          <div className={classes.search}>
            <SearchBox
              onSearch={(key?: string) => setSearch(key?.toLowerCase())}
              autoSearch
              autoFocus
              collapsable={false}
              placeholder='Search recurring'
              classes={{
                root: classes.searchBar,
                input: classes.searchInput,
              }}
            />
          </div>
        </div>

        {!filteredScheduledTransactions.size ? (
          <ZeroStateView
            title="We couldn't find any scheduled transactions"
            description='Try resetting the search'
            qIllustration='accounts'
            size='small'
            sx={{ height: listHeight, minHeight: LIST_MIN_HEIGHT, maxHeight: LIST_MAX_HEIGHT }}
          />
        ) : (
          <List
            component='nav'
            aria-label='setup recurring options'
            sx={{
              px: 2,
              py: 0,
              height: listHeight,
              minHeight: LIST_MIN_HEIGHT,
              maxHeight: LIST_MAX_HEIGHT,
              overflow: 'auto',
            }}
          >
            {filteredScheduledTransactions.map((scheduledTransaction: ScheduledTransaction, index: number) => {
              const { transaction } = scheduledTransaction;
              if (!transaction) {
                return null;
              }

              return (
                <ListItem
                  key={scheduledTransaction.id || scheduledTransaction.clientId}
                  className={classes.containerListItem}
                  disablePadding
                >
                  <ListItemButton
                    id={`existing-recurring-transaction-${index}`}
                    onClick={() => onSelected?.(scheduledTransaction)}
                    className={classes.listItemButton}
                  >
                    <ListItemIcon sx={{ minWidth: 56 }}>
                      <ScheduledTransactionAvatar scheduledTransaction={scheduledTransaction} />
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <>
                          {transaction.payee}
                          {scheduledTransaction.isCompleted && (
                            <Chip label='Canceled' variant='tonal' color='inactive' size='small' sx={{ ml: 1 }} />
                          )}
                        </>
                      }
                      primaryTypographyProps={{ noWrap: true }}
                      secondary={scheduledTransactionsUtils.frequencyAliasFromRecurrence(
                        scheduledTransaction.recurrence,
                      )}
                      sx={{ flexGrow: 2 }}
                    />

                    <ListItemText
                      primary={
                        <AmountField
                          showSign={ShowSignEnum.POSITIVE_ONLY}
                          showColor={ShowColorEnum.POSITIVE_ONLY}
                          value={transaction.amount}
                        />
                      }
                      secondary={accountsUtils.getAccountString(transaction.accountId)}
                      secondaryTypographyProps={{ style: { textAlign: 'right' }, noWrap: true }}
                      primaryTypographyProps={{ style: { textAlign: 'right' } }}
                    />
                    <NavigateNextRoundedIcon />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      {onCreate && (
        <div className={classes.actionRow}>
          <Typography variant='paragraphRegular' color='text.secondary' style={{ marginRight: 8 }}>
            Don&apos;t see it?
          </Typography>

          <Button variant='text' onClick={onCreate}>
            Create new recurring
          </Button>
        </div>
      )}
    </>
  );

  if (rootModalDrawer) {
    return (
      <StandardDrawer
        closeTransition={rootModalDrawer.closeTransition}
        variant='overlay'
        onClose={onClose ?? (() => {})}
        open={Boolean(open)}
        showScrim={false}
        width={rootModalDrawer.width ?? 'Medium'}
      >
        <StdDialogTitle
          variant='drawer'
          id='link-existing-recurring-title'
          onBack={onBack}
          onClose={onClose ?? (() => {})}
          showBackButton={showBackButton}
          showCloseButton
        >
          Link to existing recurring
        </StdDialogTitle>

        {content}
      </StandardDrawer>
    );
  }

  return (
    <StdDialog size='md' title='Link to existing recurring' {...otherProps}>
      {content}
    </StdDialog>
  );
};

export default SelectScheduledTransactionDialog;
